import { isPlatformBrowser, isPlatformServer } from '@angular/common';
import { PLATFORM_ID, TransferState, WritableSignal, effect, inject, makeStateKey, signal } from '@angular/core';

/**
 * Creates a WritableSignal that leverages TransferState for SSR.
 *
 * On the server, it sets the initial value in TransferState.
 * On the browser, it attempts to retrieve the value from TransferState first,
 * falling back to the provided initialValue if not found.
 *
 * @template T The type of the value held by the signal.
 * @param key A unique string key to identify the state in TransferState.
 * @param initialValue The initial value of the signal if not found in TransferState or on the server.
 * @returns A WritableSignal<T> synchronized with TransferState.
 */
export function linkedTransferState<T>(key: string, initialValue: T): WritableSignal<T> {
    const platformId = inject(PLATFORM_ID);
    const transferState = inject(TransferState);
    const stateKey = makeStateKey<T>(key);

    let resolvedInitialValue: T;

    if (isPlatformBrowser(platformId)) {
        // On the browser, try to get the state from TransferState
        resolvedInitialValue = transferState.get(stateKey, initialValue);
        // Optional: Remove the key after reading to prevent it from being reused
        // if the component is destroyed and re-created on the client without a new SSR navigation.
        // transferState.remove(stateKey);
    } else {
        // On the server, or if not in browser (e.g. pre-rendering), use the initial value
        resolvedInitialValue = initialValue;
    }

    const newSignal = signal(resolvedInitialValue);

    if (isPlatformServer(platformId)) {
        //  Updating TransferState on server-side signal change:
        effect(() => {
            transferState.set(stateKey, newSignal());
        });
    }

    return newSignal;
}
